import { CONSUMER_ENVIRONMENT_LABELS, CONSUMER_STATUS_LABELS, type Consumer } from '@easyhelp/shared';
import BlockIcon from '@mui/icons-material/Block';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HistoryIcon from '@mui/icons-material/History';
import WebhookIcon from '@mui/icons-material/Webhook';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CopyButton } from './CopyButton';
import { lastTokenLabel, ticketsReceivedLabel } from './format';

export interface ConsumersTableProps {
  consumers: Consumer[];
  onEdit: (consumer: Consumer) => void;
  onRevoke: (consumer: Consumer) => void;
  onHistory: (consumer: Consumer) => void;
  /** Notifica di cambio stato (DEC-013): configurabile se attivo, in sola lettura se revocato. */
  onWebhook: (consumer: Consumer) => void;
}

/**
 * Il registro in tabella: stato, prodotto, ambiente, committenti, client_id copiabile, ultimo token,
 * ticket e notifica di cambio stato.
 */
export function ConsumersTable({ consumers, onEdit, onRevoke, onHistory, onWebhook }: ConsumersTableProps) {
  return (
    <TableContainer>
      <Table size="small" aria-label="Registro dei consumer">
        <TableHead>
          <TableRow>
            <TableCell>Consumer</TableCell>
            <TableCell>Prodotto</TableCell>
            <TableCell>Ambiente</TableCell>
            <TableCell>Committenti autorizzati</TableCell>
            <TableCell>client_id</TableCell>
            <TableCell>Ultimo token</TableCell>
            <TableCell>Ticket ricevuti</TableCell>
            <TableCell>Notifica</TableCell>
            <TableCell align="right">Azioni</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {consumers.map((consumer) => {
            const revoked = consumer.status === 'revocato';
            return (
              <TableRow key={consumer.id} sx={{ opacity: revoked ? 0.7 : 1 }}>
                <TableCell>
                  <Stack spacing={0.5} alignItems="flex-start">
                    <Typography fontWeight={500}>{consumer.name}</Typography>
                    <Chip
                      size="small"
                      label={CONSUMER_STATUS_LABELS[consumer.status]}
                      color={revoked ? 'default' : 'success'}
                      variant={revoked ? 'outlined' : 'filled'}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  {consumer.product.name}{' '}
                  <Typography component="span" variant="body2" color="text.secondary">
                    ({consumer.product.prefix})
                  </Typography>
                </TableCell>
                <TableCell>{CONSUMER_ENVIRONMENT_LABELS[consumer.environment]}</TableCell>
                <TableCell>{consumer.authorizedCommittenti.join(', ')}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography
                      component="code"
                      variant="body2"
                      sx={{ fontFamily: (theme) => theme.typography.fontFamilyMonospace }}
                    >
                      {consumer.clientId}
                    </Typography>
                    <CopyButton value={consumer.clientId} label={`client_id di ${consumer.name}`} />
                  </Stack>
                </TableCell>
                <TableCell>{lastTokenLabel(consumer.lastTokenAt)}</TableCell>
                <TableCell>{ticketsReceivedLabel(consumer.ticketsReceived)}</TableCell>
                <TableCell>
                  {consumer.webhookEnabled ? (
                    <Chip size="small" label="Attiva" color="success" variant="outlined" />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Non attiva
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Storico">
                      <IconButton
                        aria-label={`Storico di ${consumer.name}`}
                        size="small"
                        onClick={() => onHistory(consumer)}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Notifica di cambio stato">
                      <IconButton
                        aria-label={`Notifica di cambio stato di ${consumer.name}`}
                        size="small"
                        onClick={() => onWebhook(consumer)}
                      >
                        <WebhookIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifica">
                      <IconButton
                        aria-label={`Modifica ${consumer.name}`}
                        size="small"
                        onClick={() => onEdit(consumer)}
                      >
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!revoked && (
                      <Tooltip title="Revoca">
                        <IconButton
                          aria-label={`Revoca ${consumer.name}`}
                          size="small"
                          color="error"
                          onClick={() => onRevoke(consumer)}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
